// src/pages/FiatExchange/hooks/useCoinbaseSecureToken.ts
import { useEffect, useMemo, useState } from 'react'

import { useQuery } from '@tanstack/react-query'

import { useWalletConnections } from '@src/hooks/useWalletConnections'
import { SessionTokenRequest, SessionTokenResponse } from '@src/types/coinbase'
import { createContextLogger } from '@src/utils'

import { cbGetSessionToken } from '../api/coinbase.api'

const logger = createContextLogger('useCoinbaseSecureToken')
const TOKEN_EXPIRY_BUFFER = 30 // Seconds before expiry to trigger refresh

export const useCoinbaseSecureToken = (payload: SessionTokenRequest) => {
    const { evmWalletAddress, isEVMConnected } = useWalletConnections()
    // Store the token expiration timestamp separately.
    const [expiresAt, setExpiresAt] = useState<Date | null>(null)

    // Validate that the payload is complete
    const isPayloadComplete = useMemo(() => {
        return !!(
            payload.addresses &&
            payload.addresses.length > 0 &&
            payload.addresses[0].address &&
            payload.addresses[0].blockchains &&
            payload.addresses[0].blockchains.length > 0 &&
            payload.assets &&
            payload.assets.length > 0
        )
    }, [payload])

    logger.debug('useCoinbaseSecureToken initialized with payload:', {
        payloadAddresses: payload.addresses?.length,
        payloadAssets: payload.assets,
        evmWalletAddress,
        isEVMConnected,
        isPayloadComplete,
    })

    const tokenQuery = useQuery<SessionTokenResponse, Error>({
        // Include wallet address in query key so that the token refetches when it changes.
        queryKey: ['coinbaseSessionToken', evmWalletAddress, payload],
        queryFn: async () => {
            logger.info('Fetching new Coinbase session token')
            const startTime = performance.now()
            try {
                if (!isPayloadComplete) {
                    throw new Error('Cannot fetch session token: incomplete payload')
                }
                const response = await cbGetSessionToken(payload)
                const endTime = performance.now()
                logger.success('Session token fetched successfully in', Math.round(endTime - startTime), 'ms')
                return response
            } catch (error) {
                logger.error('Failed to fetch session token:', error)
                throw error
            }
        },
        enabled: isEVMConnected && !!evmWalletAddress && isPayloadComplete,
        // Dynamically refresh the token: refresh 30 seconds before it expires (minimum interval 5s).
        refetchInterval: () => {
            if (!expiresAt) return 5000
            const now = new Date()
            const timeUntilExpiry = expiresAt.getTime() - now.getTime()
            return Math.max(timeUntilExpiry - TOKEN_EXPIRY_BUFFER * 1000, 5000)
        },
        refetchOnWindowFocus: true,
        staleTime: 1000 * TOKEN_EXPIRY_BUFFER,
        retry: 2,
    })

    // Use useEffect to update expiresAt when new token data is received.
    useEffect(() => {
        if (tokenQuery.data) {
            // Manually calculate expiration: token is valid for 2 minutes from now.
            const newExpiry = new Date(Date.now() + 2 * 60 * 1000)
            setExpiresAt(newExpiry)

            const timeUntilExpiry = Math.round((newExpiry.getTime() - Date.now()) / 1000)
            logger.info(`Session token set. Valid for ${timeUntilExpiry} seconds (until ${newExpiry.toISOString()})`)
            logger.debug('Token details:', {
                channelId: tokenQuery.data.channelId,
                tokenLength: tokenQuery.data.token.length,
                expiryTime: newExpiry,
            })
        }
    }, [tokenQuery.data])

    // Log query status changes
    useEffect(() => {
        if (tokenQuery.isLoading) {
            logger.debug('Session token query loading')
        }
        if (tokenQuery.isFetching) {
            logger.debug('Session token query fetching')
        }
        if (tokenQuery.isError && tokenQuery.error) {
            logger.error('Session token query error:', tokenQuery.error)
        }
    }, [tokenQuery.isLoading, tokenQuery.isFetching, tokenQuery.isError, tokenQuery.error])

    const isTokenValid = () => {
        if (!expiresAt) {
            logger.debug('Token validity check: No expiration time set')
            return false
        }
        const now = new Date()
        const isValid = expiresAt > now
        const remainingTimeMs = expiresAt.getTime() - now.getTime()

        if (isValid) {
            logger.debug(`Token validity check: Valid for ${Math.round(remainingTimeMs / 1000)} more seconds`)
        } else {
            logger.warn('Token validity check: Token has expired')
        }

        return isValid
    }

    // Log when returned state changes
    useEffect(() => {
        const isReady = !!tokenQuery.data?.token && isTokenValid()
        if (isReady) {
            logger.info('Session token is ready for use')
        } else if (tokenQuery.data?.token) {
            logger.warn('Session token exists but is not valid')
        }
    }, [tokenQuery.data, isTokenValid])

    return {
        sessionToken: tokenQuery.data?.token,
        isSessionTokenFetching: tokenQuery.isFetching,
        isSessionTokenReady: !!tokenQuery.data?.token && isTokenValid(),
        sessionTokenError: tokenQuery.error,
    }
}
